import React, { useEffect, useState } from 'react';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import OrderChat from '../../../components/messaging/OrderChat';
import Card from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';

const STATUS_BADGE = {
  placed: 'bg-blue-100 text-blue-700',
  accepted: 'bg-indigo-100 text-indigo-700',
  shipped: 'bg-amber-100 text-amber-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const NEXT_STATUS = {
  placed: 'accepted',
  accepted: 'shipped',
  shipped: 'delivered',
};

const NEXT_LABEL = {
  placed: 'Accept order',
  accepted: 'Mark shipped',
  shipped: 'Mark delivered',
};

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatOpenId, setChatOpenId] = useState(null);

  const load = async () => {
    try {
      const data = await marketplaceApi.myOrders();
      setOrders(data);
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const advance = async (order) => {
    const next = NEXT_STATUS[order.status];
    if (!next) return;
    try {
      await marketplaceApi.updateOrderStatus(order.id, { status: next });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  const togglePayment = async (order) => {
    const next = order.payment_status === 'paid' ? 'pending' : 'paid';
    try {
      await marketplaceApi.updateOrderStatus(order.id, { payment_status: next });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  const cancel = async (order) => {
    if (!window.confirm(`Cancel order #${order.id}?`)) return;
    try {
      await marketplaceApi.updateOrderStatus(order.id, { status: 'cancelled' });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading orders…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Incoming orders</h1>
        <p className="mt-1 text-sm text-gray-600">
          Orders buyers have placed against your FPO's listings. Move them through the fulfilment workflow as you go.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {orders.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">
            No orders yet. Publish a listing and share your FPO with buyers.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {orders.map((order) => (
            <Card key={order.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500">Order #{order.id}</span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${STATUS_BADGE[order.status]}`}>
                      {order.status}
                    </span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      Payment: {order.payment_status}
                    </span>
                  </div>
                  <h3 className="mt-1 text-base font-semibold text-gray-900">
                    {order.buyer_full_name || order.buyer_username}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Placed {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-green-800">{formatCurrency(order.total)}</p>
                  <p className="text-xs text-gray-500">{order.items.length} item{order.items.length === 1 ? '' : 's'}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Deliver to</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{order.delivery_contact_name}</p>
                  <p className="text-sm text-gray-700">{order.delivery_contact_phone}</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{order.delivery_address}</p>
                  {order.notes && (
                    <p className="mt-2 text-xs italic text-gray-500">Note: {order.notes}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Items</p>
                  <ul className="mt-1 flex flex-col gap-1 text-sm">
                    {order.items.map((it) => (
                      <li key={it.id} className="flex justify-between text-gray-700">
                        <span>
                          {it.listing_title_snapshot}
                          <span className="ml-1 text-gray-500">
                            · {Number(it.quantity).toFixed(2)} {it.unit_snapshot}
                          </span>
                        </span>
                        <span className="font-medium text-gray-900">{formatCurrency(it.line_total)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                {NEXT_STATUS[order.status] && (
                  <button
                    type="button"
                    onClick={() => advance(order)}
                    className="rounded-lg bg-green-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-800"
                  >
                    {NEXT_LABEL[order.status]} →
                  </button>
                )}
                {order.status !== 'cancelled' && order.status !== 'delivered' && (
                  <button
                    type="button"
                    onClick={() => cancel(order)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                  >
                    Cancel order
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setChatOpenId(chatOpenId === order.id ? null : order.id)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  {chatOpenId === order.id ? 'Hide messages' : 'Messages'}
                </button>
                <button
                  type="button"
                  onClick={() => togglePayment(order)}
                  className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Mark payment {order.payment_status === 'paid' ? 'pending' : 'paid'}
                </button>
              </div>
              {chatOpenId === order.id && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <OrderChat
                    orderId={order.id}
                    currentUserId={user.id}
                    allowCounterOffer={true}
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
